import axios, { AxiosInstance, AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import {
  Account,
  FixedExpense,
  ForecastParams,
  ForecastResponse,
  ForecastSnapshot,
  Invoice,
  Payment,
  PayrollExpense,
  Project,
  ProjectExpense,
  ProjectPayment,
  ProjectResourceAllocation,
  PurchaseOrder,
  ResourceType,
  Tag,
  UpsertAccount,
  UpsertFixedExpense,
  UpsertForecastSnapshot,
  UpsertInvoice,
  UpsertPayment,
  UpsertPayrollExpense,
  UpsertProject,
  UpsertProjectExpense,
  UpsertProjectPayment,
  UpsertProjectResourceAllocation,
  UpsertPurchaseOrder,
  UpsertResourceType,
  UpsertTag,
  UpsertProjectBudgetCategory,
} from '../types';
import { DomainUtils } from '../utils/domain';

// Create a function to get the current organization ID
let getCurrentOrganizationId: (() => number | undefined) | null = null;

// Function to set the getter
export const setOrganizationIdGetter = (getter: () => number | undefined) => {
  getCurrentOrganizationId = getter;
};

const isoDateTimeFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?(?:Z|[-+]\d{2}:\d{2})?$/;
const isoDateFormat = /^\d{4}-\d{2}-\d{2}$/;

function isIsoDateTimeString(value: any): boolean {
  return value && typeof value === 'string' && isoDateTimeFormat.test(value);
}

function isIsoDateString(value: any): boolean {
  return value && typeof value === 'string' && isoDateFormat.test(value);
}

function convertToDayjs(body: any): any {
  if (body === null || body === undefined || typeof body !== 'object') {
    return body;
  }

  // Fields that should never be converted to dates even if they match the date format
  const excludedFields = ['name', 'description', 'po_number', 'invoice_number'];

  for (const key of Object.keys(body)) {
    const value = body[key];

    // Skip conversion for excluded fields
    if (excludedFields.includes(key)) {
      continue;
    }

    if (isIsoDateTimeString(value) || isIsoDateString(value)) {
      body[key] = dayjs(value);
    } else if (Array.isArray(value)) {
      // Handle arrays - convert each element
      body[key] = value.map(item => {
        if (isIsoDateTimeString(item) || isIsoDateString(item)) {
          return dayjs(item);
        } else if (typeof item === 'object') {
          return convertToDayjs(item);
        }
        return item;
      });
    } else if (typeof value === 'object') {
      body[key] = convertToDayjs(value);
    }
  }
  return body;
}

function convertToString(body: any): any {
  if (body === null || body === undefined || typeof body !== 'object') {
    return body;
  }
  for (const key of Object.keys(body)) {
    const value = body[key];
    if (dayjs.isDayjs(value)) {
      // Convert to date-only format (YYYY-MM-DD) for business dates
      // The backend will handle these as date objects
      body[key] = value.format('YYYY-MM-DD');
    } else if (Array.isArray(value)) {
      // Handle arrays - convert each element
      body[key] = value.map(item => {
        if (dayjs.isDayjs(item)) {
          return item.format('YYYY-MM-DD');
        } else if (typeof item === 'object') {
          return convertToString(item);
        }
        return item;
      });
    } else if (typeof value === 'object') {
      body[key] = convertToString(value);
    }
  }
  return body;
}

// Create an axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: DomainUtils.getAPIUrl(), // Use DomainUtils to get the API URL
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true, // Include cookies in cross-site requests
  transformResponse: [
    (data: any) => {
      try {
        const jsonData = JSON.parse(data);
        return convertToDayjs(jsonData);
      } catch (error) {
        return data;
      }
    },
  ],
  transformRequest: [
    (data: any) => {
      const transformedData = convertToString(data);
      return JSON.stringify(transformedData);
    },
  ],
});

// Add request interceptor to handle subdomain
api.interceptors.request.use(config => {
  // Get the subdomain using DomainUtils
  const subdomain = DomainUtils.getSubdomain();

  // Add subdomain to headers for backend validation
  config.headers['X-Organization-Subdomain'] = subdomain || '';
  // Get the current organization ID using the getter
  if (getCurrentOrganizationId) {
    const orgId = getCurrentOrganizationId();
    if (orgId) {
      config.headers['X-Organization-Id'] = orgId.toString();
    }
  }

  return config;
});

// Add response interceptor to handle errors
api.interceptors.response.use(
  response => response,
  error => {
    // Log error details for debugging
    console.log('API Error:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
      url: error.config?.url,
      baseURL: error.config?.baseURL,
      headers: error.config?.headers,
    });

    if (error.response?.status === 401) {
      // Only redirect to login if we're not already on the login page
      if (!DomainUtils.isLoginPath()) {
        // Get the current subdomain
        // Use React Router's navigate to maintain domain context
        window.location.href = `/login`;
      }
    }

    // Handle CORS errors
    if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
      console.error('CORS or network error detected. Please check your backend configuration.');
      // Try to get more details about the error
      if (error.request) {
        console.error('Request details:', {
          url: error.request.url,
          method: error.request.method,
          headers: error.request.headers,
        });
      }
    }

    // Handle 500 errors
    if (error.response?.status === 500) {
      console.error('Server error:', error.response.data);
    }

    return Promise.reject(error);
  }
);

// Accounts
export const getAccounts = (): Promise<AxiosResponse<Account[]>> => api.get('/api/accounts');
export const addAccount = (accountData: UpsertAccount): Promise<AxiosResponse<Account>> =>
  api.post('/api/accounts', accountData);
export const updateAccount = (accountData: UpsertAccount): Promise<AxiosResponse<Account>> =>
  api.post('/api/accounts', accountData);

// Fixed Expenses
export const getFixedExpenses = (): Promise<AxiosResponse<FixedExpense[]>> =>
  api.get('/api/fixed-expenses');
export const addFixedExpense = (
  expenseData: UpsertFixedExpense
): Promise<AxiosResponse<FixedExpense>> => api.post('/api/fixed-expenses', expenseData);
export const updateFixedExpense = (
  expenseData: UpsertFixedExpense
): Promise<AxiosResponse<FixedExpense>> => api.put('/api/fixed-expenses', expenseData);
export const deleteFixedExpense = (id: string | number): Promise<AxiosResponse<null>> =>
  api.delete('/api/fixed-expenses', { data: { id } });

// Payroll
export const getPayroll = (): Promise<AxiosResponse<PayrollExpense[]>> =>
  api.get('/api/payroll-expenses');
export const addPayrollExpense = (
  expenseData: UpsertPayrollExpense
): Promise<AxiosResponse<PayrollExpense>> => api.post('/api/payroll-expenses', expenseData);
export const updatePayrollExpense = (
  expenseData: UpsertPayrollExpense
): Promise<AxiosResponse<PayrollExpense>> => api.put('/api/payroll-expenses', expenseData);
export const deletePayrollExpense = (id: string | number): Promise<AxiosResponse<null>> =>
  api.delete('/api/payroll-expenses', { data: { id } });

// Projects
// Payments
export const createPayment = (payment: UpsertPayment): Promise<AxiosResponse<Payment>> =>
  api.post('/api/payments', payment);

export const updatePayment = (payment: UpsertPayment): Promise<AxiosResponse<Payment>> =>
  api.put('/api/payments', payment);

export const deletePayment = (id: number | string): Promise<AxiosResponse<null>> =>
  api.delete(`/api/payments`, { data: { id } });

// Expenses
export const getProjectExpenses = (projectId: number): Promise<AxiosResponse<ProjectExpense[]>> =>
  api.get(`/api/project-expenses`, { params: { project_id: projectId } });

export const createProjectExpense = (
  projectExpense: UpsertProjectExpense
): Promise<AxiosResponse<ProjectExpense>> => api.post('/api/project-expenses', projectExpense);

export const updateProjectExpense = (
  projectExpense: UpsertProjectExpense
): Promise<AxiosResponse<ProjectExpense>> => api.put('/api/project-expenses', projectExpense);

export const deleteProjectExpense = (id: number | string): Promise<AxiosResponse<null>> =>
  api.delete(`/api/project-expenses`, { data: { id } });

export const importProjectExpenses = (
  projectId: number,
  file: File
): Promise<
  AxiosResponse<{
    imported: number;
    skipped: number;
    failed: number;
    before_start_count: number;
    errors: string[];
    warnings: string[];
  }>
> => {
  const formData = new FormData();
  formData.append('file', file);

  // Override the default transformRequest and Content-Type for file upload
  return api.post(`/api/project-expenses/import?project_id=${projectId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    transformRequest: [data => data], // Don't transform FormData
  });
};

export const getProjects = (): Promise<AxiosResponse<Project[]>> => {
  return api.get('/api/current-projects');
};
export const addProject = (projectData: UpsertProject): Promise<AxiosResponse<Project>> =>
  api.post('/api/current-projects', projectData);
export const updateProject = (projectData: UpsertProject): Promise<AxiosResponse<Project>> => {
  return api.put('/api/current-projects', {
    ...projectData,
  });
};
export const deleteProject = (id: number): Promise<AxiosResponse<null>> =>
  api.delete('/api/current-projects', { data: { id } });

export const copyProject = (copyData: {
  source_project_id: number;
  name: string;
  description?: string;
  start_date: string;
  savings_percentage: number;
  tag_ids?: number[];
  budget_categories?: UpsertProjectBudgetCategory[];
  copy_budget_category_associations?: boolean;
  resource_allocations?: ProjectResourceAllocation[];
}): Promise<AxiosResponse<Project>> => api.post('/api/copy-project', copyData);

// Project Payments
export const getProjectPayments = (
  projectId: string | number
): Promise<AxiosResponse<ProjectPayment[]>> => api.get(`/api/project-payments/${projectId}`);
export const addProjectPayment = (
  paymentData: UpsertProjectPayment
): Promise<AxiosResponse<ProjectPayment>> => api.post('/api/project-payment', paymentData);
export const updateProjectPayment = (
  paymentData: UpsertProjectPayment
): Promise<AxiosResponse<ProjectPayment>> => api.put('/api/project-payment', paymentData);
export const deleteProjectPayment = (id: string | number): Promise<AxiosResponse<null>> =>
  api.delete('/api/project-payment', { data: { id } });

// Forecast
export const generateForecast = (
  params: ForecastParams & { status: string }
): Promise<AxiosResponse<ForecastResponse>> => api.post('/api/forecast', params);
export const getForecastSnapshots = (): Promise<AxiosResponse<ForecastSnapshot[]>> =>
  api.get('/api/snapshots');
export const saveForecastSnapshot = (
  snapshotData: UpsertForecastSnapshot
): Promise<AxiosResponse<ForecastSnapshot>> => api.post('/api/snapshots', snapshotData);
export const updateForecastSnapshot = (
  snapshotData: UpsertForecastSnapshot
): Promise<AxiosResponse<ForecastSnapshot>> => api.put('/api/snapshots', snapshotData);
export const deleteForecastSnapshot = (
  id: string | number
): Promise<AxiosResponse<{ message: string }>> => api.delete('/api/snapshots', { data: { id } });
export const restoreForecastSnapshot = (
  id: string | number,
  currentStartDate?: dayjs.Dayjs,
  currentEndDate?: dayjs.Dayjs
): Promise<
  AxiosResponse<{
    success: boolean;
    backup_snapshot: ForecastSnapshot;
    message: string;
  }>
> =>
  api.post(`/api/snapshots/${id}/restore`, {
    current_start_date: currentStartDate?.format('YYYY-MM-DD'),
    current_end_date: currentEndDate?.format('YYYY-MM-DD'),
  });

// Forecast Cleanup
export const checkUncommittedCards = (
  startDate: string
): Promise<AxiosResponse<{
  has_uncommitted: boolean;
  count: number;
  cards: Array<{
    card_id: number;
    card_type: 'projectExpense' | 'purchaseOrder';
    project_id: number;
    project_name: string;
    description: string;
    card_date: string;
    amount: number;
    remaining_amount: number;
  }>;
  by_project: {
    [projectId: number]: {
      project_id: number;
      project_name: string;
      count: number;
    };
  };
}>> => api.get('/api/forecast-cleanup/check-uncommitted', { params: { start_date: startDate } });

export const pullCardsForward = (
  cardIds: number[],
  targetDate: string
): Promise<AxiosResponse<{
  success: boolean;
  count: number;
  message: string;
}>> => api.post('/api/forecast-cleanup/pull-cards-forward', { card_ids: cardIds, target_date: targetDate });

// Purchase Orders
export const getPurchaseOrders = (
  project_id?: number
): Promise<
  AxiosResponse<{
    pos: PurchaseOrder[];
    projects: string[];
    project_expenses: any;
  }>
> => api.get('/api/purchase-orders', { params: { project_id } });

export const addPurchaseOrder = (
  poData: UpsertPurchaseOrder
): Promise<AxiosResponse<PurchaseOrder>> => api.post('/api/purchase-orders', poData);

export const updatePurchaseOrder = (
  poData: UpsertPurchaseOrder
): Promise<AxiosResponse<PurchaseOrder>> => {
  return api.put('/api/purchase-orders', poData);
};

export const deletePurchaseOrder = (id: string | number): Promise<AxiosResponse<null>> =>
  api.delete('/api/purchase-orders', { data: { id } });

// Invoices
export const getInvoices = (): Promise<AxiosResponse<Invoice[]>> => api.get('/api/invoices');

export const addInvoice = (invoiceData: UpsertInvoice): Promise<AxiosResponse<Invoice[]>> =>
  api.post('/api/invoices', invoiceData);

export const updateInvoice = (invoiceData: UpsertInvoice): Promise<AxiosResponse<Invoice>> =>
  api.put('/api/invoices', invoiceData);

export const deleteInvoice = (id: string | number): Promise<AxiosResponse<null>> =>
  api.delete('/api/invoices', { data: { id } });

// Anticipated Projects
export const getAnticipatedProjects = (): Promise<
  AxiosResponse<{
    projects: Project[];
    archived: { name: string }[];
  }>
> => api.get('/api/anticipated-projects');

// Project Creation Types
export const addAnticipatedProject = (
  projectData: UpsertProject
): Promise<AxiosResponse<Project>> => api.post('/api/anticipated-projects', projectData);

export const updateAnticipatedProject = (
  projectData: UpsertProject
): Promise<AxiosResponse<Project>> => api.put('/api/anticipated-projects', projectData);

export const deleteAnticipatedProject = (id: string): Promise<AxiosResponse<Project>> =>
  api.delete('/api/anticipated-projects', { data: { id } });

export const archiveProject = (id: string): Promise<AxiosResponse<Project>> =>
  api.post('/api/archive-project', { id });

export const restoreProject = (id: string): Promise<AxiosResponse<Project>> =>
  api.post('/api/restore-project', { id });

export const convertAnticipatedToCurrent = (id: string): Promise<AxiosResponse<Project>> =>
  api.post('/api/convert-anticipated-to-current', { id });

export const convertCurrentToAnticipated = (id: string): Promise<AxiosResponse<Project>> =>
  api.post('/api/convert-current-to-anticipated', { id });

// Tags
export const getTags = (): Promise<AxiosResponse<Tag[]>> => api.get('/api/tags');
export const createTag = (tag: UpsertTag): Promise<AxiosResponse<Tag>> =>
  api.post('/api/tags', tag);
export const updateTag = (tag: UpsertTag): Promise<AxiosResponse<Tag>> => api.put('/api/tags', tag);
export const deleteTag = (id: number | string): Promise<AxiosResponse<null>> =>
  api.delete(`/api/tags`, { data: { id } });

export const getProjectCategoryTags = (projectId: number): Promise<AxiosResponse<Tag[]>> =>
  api.get(`/api/tags/project-categories/${projectId}`);

// Resource Types
export const getResourceTypes = (): Promise<AxiosResponse<ResourceType[]>> =>
  api.get('/api/resource-types');
export const createResourceType = (
  resourceType: UpsertResourceType
): Promise<AxiosResponse<ResourceType>> => api.post('/api/resource-types', resourceType);
export const updateResourceType = (
  id: number,
  resourceType: UpsertResourceType
): Promise<AxiosResponse<ResourceType>> => api.put(`/api/resource-types/${id}`, resourceType);
export const deleteResourceType = (id: number | string): Promise<AxiosResponse<null>> =>
  api.delete(`/api/resource-types/${id}`);

// Project Resource Allocations
export const getProjectResourceAllocations = (
  projectId?: number,
  activeOnly?: boolean
): Promise<AxiosResponse<ProjectResourceAllocation[]>> => {
  const params: any = {};
  if (projectId) params.project_id = projectId;
  if (activeOnly !== undefined) params.active_only = activeOnly;
  return api.get('/api/project-resource-allocations', { params });
};
export const createProjectResourceAllocation = (
  allocation: UpsertProjectResourceAllocation
): Promise<AxiosResponse<ProjectResourceAllocation>> =>
  api.post('/api/project-resource-allocations', allocation);
export const updateProjectResourceAllocation = (
  allocation: UpsertProjectResourceAllocation
): Promise<AxiosResponse<ProjectResourceAllocation>> =>
  api.put(`/api/project-resource-allocations/${allocation.id}`, allocation);
export const deleteProjectResourceAllocation = (
  id: number | string
): Promise<AxiosResponse<null>> =>
  api.delete(`/api/project-resource-allocations`, { data: { id } });

export default api;
